import './styles/style.scss';

import HowToVoteIcon from '@mui/icons-material/HowToVote';
import {
  Grid2,
  AlertTitle,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination
} from '@mui/material';
import Alert from '@mui/material/Alert';
import { User } from '@thro-types/users';

import { parseAsInteger, useQueryState } from 'nuqs';
import { useTranslation } from 'react-i18next';

import {
  useMarkVotedMutation,
  useMyElections,
  useMyElectionsCounts
} from 'api/hooks/polyas/polyas';
import { translations } from 'locales/i18n';
import usePageHeading from 'utils/hooks/usePageHeading';

import { MyElectionsSkeleton } from '../Elections/components/election-skeletons';
import { ElectionStatusFilter } from '../Elections/components/election-status-filter';
import { UserElectionCard } from '../Elections/components/user-election-card';

interface Props {
  currentUser: User;
}

const PAGE_SIZE_OPTIONS = [6, 12, 24, 48];

export default function MyElections({ currentUser }: Props) {
  const { t } = useTranslation();
  usePageHeading(t(translations.pages.polyas.myElections()));
  const [page, setPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [limit, setLimit] = useQueryState('limit', parseAsInteger.withDefault(6));
  const [state, setState] = useQueryState('state');

  const {
    data: userElections,
    isLoading,
    error,
    refetch
  } = useMyElections({ params: { page, limit, state } });
  const { data: userElectionsCounts } = useMyElectionsCounts();
  const { markAsVoted, unmarkAsVoted } = useMarkVotedMutation();

  const pendingVotesCount = userElections?.data.filter(
    e => e.status === 'active' && e.markedAsVoted !== true
  ).length;

  const handlePageChange = (_event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1); // Reset to first page when page size changes
    refetch();
  };

  const handleMarkAsVoted = (electionId: string, marked: boolean) => {
    if (marked) {
      markAsVoted.mutateAsync({ electionId });
    } else {
      unmarkAsVoted.mutateAsync({ electionId });
    }
  };

  if (isLoading) {
    return <MyElectionsSkeleton />;
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {t(translations.pages.polyas.detail.errorLoadingElection())}: {error.message}
      </Alert>
    );
  }

  return (
    <Grid2 size={12}>
      {/* Info */}
      {!!pendingVotesCount && pendingVotesCount > 0 && (
        <Alert severity="info">
          <AlertTitle>{t(translations.pages.polyas.myElectionsPage.pendingVotes())}</AlertTitle>
          {t(translations.pages.polyas.myElectionsPage.pendingVotesDescription(), {
            count: pendingVotesCount,
            plural: pendingVotesCount !== 1 ? 'en' : '',
            plural2: pendingVotesCount !== 1 ? ' ' : ''
          })}
        </Alert>
      )}
      {/* Filter */}
      <Grid2 size={12} container spacing={1} paddingY={1}>
        <ElectionStatusFilter
          value={state ?? 'all'}
          onChange={setState}
          counts={userElectionsCounts}
        />
      </Grid2>
      {/* Filter ende */}

      {!userElections || userElections?.data.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Box
            sx={{
              mb: 2,
              p: 2,
              borderRadius: '50%',
              bgcolor: 'action.hover',
              display: 'inline-flex'
            }}
          >
            <HowToVoteIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
          </Box>
          <Typography variant="h6" gutterBottom>
            {t(translations.pages.polyas.noElectionsFound())}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320, mx: 'auto' }}>
            {state === 'all'
              ? t(translations.pages.polyas.notEligibleForAnyElections())
              : t(translations.pages.polyas.noActiveElections())}
          </Typography>
        </Box>
      ) : (
        <Grid2 container spacing={2} size={12} pb={'2em'} direction={'column'}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}
          >
            {userElections.data.map(election => (
              <UserElectionCard
                key={election.id}
                election={election}
                voterStatus={!!election.markedAsVoted}
                personalizedLink={election.secureLink}
                onMarkAsVoted={marked => handleMarkAsVoted(election.id, marked)}
              />
            ))}
          </Box>
          {userElections.meta.total > 6 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                pt: 2,
                borderTop: 1,
                borderColor: 'divider'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {userElections.meta.total} {t(translations.pages.polyas.election())}
                </Typography>
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <InputLabel id="page-size-label">
                    {t(translations.pages.polyas.perPage())}
                  </InputLabel>
                  <Select
                    labelId="page-size-label"
                    value={limit}
                    label={t(translations.pages.polyas.perPage())}
                    onChange={e => handleLimitChange(Number(e.target.value))}
                  >
                    {PAGE_SIZE_OPTIONS.map(size => (
                      <MenuItem key={size} value={size}>
                        {size}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Pagination
                count={userElections.meta.totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
                siblingCount={1}
                boundaryCount={1}
              />
            </Box>
          )}
        </Grid2>
      )}
    </Grid2>
  );
}
